import { z } from 'zod';

type SearchParamPrimitive = string | number | boolean;
type SearchParamValue = SearchParamPrimitive | readonly SearchParamPrimitive[] | null | undefined;

export type ApiClientOptions = {
  baseUrl: string;
  headers?: HeadersInit;
  fetcher?: typeof fetch;
};

export type RequestOptions<TSchema extends z.ZodType> = {
  path: string;
  schema: TSchema;
  init?: RequestInit;
  responseType?: ApiResponseType;
};

export enum ApiResponseType {
  Auto = 'AUTO',
  Text = 'TEXT',
}

export type BlobRequestOptions = Omit<RequestOptions<z.ZodType>, 'schema'>;

export enum ApiErrorMessageKey {
  RequestFailed = 'api.common.requestFailed',
  InvalidJson = 'api.common.invalidJson',
  InvalidQuery = 'api.common.invalidQuery',
  ValidationFailed = 'api.common.validationFailed',
  Unauthorized = 'api.common.unauthorized',
  Forbidden = 'api.common.forbidden',
  NotFound = 'api.common.notFound',
  Conflict = 'api.common.conflict',
  Internal = 'api.common.internal',
  BadGateway = 'api.common.badGateway',
  GatewayUnavailable = 'api.gateway.proxy.unavailable',
}

export type ApiFieldError = {
  messageKey: string;
};

export type ApiErrorDetails = {
  status: number;
  code: string;
  messageKey: string;
  body: unknown;
  fields?: Record<string, ApiFieldError> | undefined;
  details?: Record<string, unknown> | undefined;
  source?: string | undefined;
};

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly messageKey: string;
  public readonly body: unknown;
  public readonly fields?: Record<string, ApiFieldError> | undefined;
  public readonly details?: Record<string, unknown> | undefined;
  public readonly source?: string | undefined;

  constructor(details: ApiErrorDetails) {
    super(details.messageKey);
    this.name = 'ApiError';
    this.status = details.status;
    this.code = details.code;
    this.messageKey = details.messageKey;
    this.body = details.body;
    this.fields = details.fields;
    this.details = details.details;
    this.source = details.source;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function apiErrorMessageKey(error: unknown): string {
  return isApiError(error) ? error.messageKey : ApiErrorMessageKey.RequestFailed;
}

export function createApiErrorDetails(status: number, body: unknown): ApiErrorDetails {
  return normalizeApiError(status, body);
}

export function createApiClient({ baseUrl, headers, fetcher = fetch }: ApiClientOptions) {
  const normalizedBaseUrl = baseUrl.replace(/\/+$/, '');

  function requestHeaders(init?: RequestInit, accept: string | null = 'application/json') {
    const mergedHeaders = new Headers(headers ?? undefined);
    if (accept) {
      mergedHeaders.set('Accept', accept);
    }
    if (init?.headers) {
      new Headers(init.headers).forEach((value, key) => {
        mergedHeaders.set(key, value);
      });
    }
    return mergedHeaders;
  }

  return {
    async request<TSchema extends z.ZodType>({
      path,
      schema,
      init,
      responseType = ApiResponseType.Auto,
    }: RequestOptions<TSchema>): Promise<z.infer<TSchema>> {
      const response = await fetcher(`${normalizedBaseUrl}${path}`, {
        ...init,
        headers: requestHeaders(
          init,
          responseType === ApiResponseType.Text ? 'text/plain' : 'application/json',
        ),
      });

      let body: unknown = null;
      if (response.status !== 204) {
        body =
          response.ok && responseType === ApiResponseType.Text
            ? await response.text()
            : await readResponseBody(response);
      }

      if (!response.ok) {
        throw new ApiError(normalizeApiError(response.status, body));
      }

      return schema.parse(body);
    },
    async requestBlob({ path, init }: BlobRequestOptions): Promise<Blob> {
      const response = await fetcher(`${normalizedBaseUrl}${path}`, {
        ...init,
        headers: requestHeaders(init, null),
      });
      if (!response.ok) {
        throw new ApiError(normalizeApiError(response.status, await readResponseBody(response)));
      }
      return response.blob();
    },
  };
}

export function jsonInit(method: string, body: unknown, headers: HeadersInit = {}): RequestInit {
  return {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...Object.fromEntries(new Headers(headers)),
    },
    body: JSON.stringify(body),
  };
}

export function toSearchParams(params: Record<string, SearchParamValue>) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    searchParams.set(key, Array.isArray(value) ? value.join(',') : String(value));
  });

  return searchParams;
}

export function nullableOptional<TSchema extends z.ZodType>(schema: TSchema) {
  return z.preprocess((value) => (value === null ? undefined : value), schema.optional());
}

export function nullableArray<TSchema extends z.ZodType>(schema: TSchema) {
  return z
    .preprocess((value) => (value === null ? undefined : value), z.array(schema).optional())
    .transform((value) => value ?? []);
}

async function readResponseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  if (isJsonContentType(contentType)) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  return response.text();
}

function isJsonContentType(contentType: string) {
  const mediaType = contentType.split(';', 1)[0]?.trim().toLowerCase() ?? '';
  return mediaType === 'application/json' || mediaType.endsWith('+json');
}

function normalizeApiError(status: number, body: unknown): ApiErrorDetails {
  const error = readRecord(readRecord(body)?.['error']);
  const fields = readFields(error?.['fields']);
  const details = readRecord(error?.['details']);
  const messageKey =
    readNonEmptyString(error?.['messageKey']) ?? defaultMessageKeyForStatus(status);

  return {
    status: readNumber(error?.['status']) ?? status,
    code: readNonEmptyString(error?.['code']) ?? defaultCodeForStatus(status),
    messageKey,
    body,
    ...(fields ? { fields } : {}),
    ...(details ? { details } : {}),
    ...(readNonEmptyString(error?.['source'])
      ? { source: readNonEmptyString(error?.['source']) }
      : {}),
  };
}

function readFields(value: unknown): Record<string, ApiFieldError> | undefined {
  const record = readRecord(value);
  if (!record) return undefined;
  const fields: Record<string, ApiFieldError> = {};
  Object.entries(record).forEach(([field, error]) => {
    const errorRecord = readRecord(error);
    const messageKey = readNonEmptyString(errorRecord?.['messageKey']);
    if (messageKey) {
      fields[field] = { messageKey };
    }
  });
  return Object.keys(fields).length ? fields : undefined;
}

function readRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function readNonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function readNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function defaultCodeForStatus(status: number): string {
  switch (status) {
    case 400:
      return 'bad_request';
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    case 404:
      return 'not_found';
    case 409:
      return 'conflict';
    case 502:
      return 'bad_gateway';
    default:
      return status >= 500 ? 'internal_error' : 'request_failed';
  }
}

function defaultMessageKeyForStatus(status: number): ApiErrorMessageKey {
  switch (status) {
    case 400:
      return ApiErrorMessageKey.ValidationFailed;
    case 401:
      return ApiErrorMessageKey.Unauthorized;
    case 403:
      return ApiErrorMessageKey.Forbidden;
    case 404:
      return ApiErrorMessageKey.NotFound;
    case 409:
      return ApiErrorMessageKey.Conflict;
    case 502:
      return ApiErrorMessageKey.BadGateway;
    default:
      return status >= 500 ? ApiErrorMessageKey.Internal : ApiErrorMessageKey.RequestFailed;
  }
}
